import { supabase } from './SupabaseConfig';
import { AuthPort, AuthCredentials, AuthResponse } from '../../domain/identity/ports/AuthPort';

export class SupabaseAuthAdapter implements AuthPort {
  async login(credentials: AuthCredentials): Promise<AuthResponse> {
    let email = credentials.email;
    let password = credentials.password;

    // Handle Staff Login (Phone/PIN)
    if (credentials.phone && credentials.pin && credentials.shopId) {
      const cleanPhone = String(credentials.phone).replace(/\D/g, '');
      email = `${cleanPhone}@${credentials.shopId}.pos.fnbos.com`.toLowerCase();
      password = `pos${String(credentials.pin).replace(/\D/g, '')}`.padEnd(6, '0');
    }

    if (!email || !password) {
      throw new Error('Thiếu thông tin đăng nhập');
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;
    if (!data.user) throw new Error('Không tìm thấy người dùng');

    return {
      uid: data.user.id,
      email: data.user.email || '',
      metadata: data.user.user_metadata
    };
  }

  async loginWithProvider(provider: 'google'): Promise<AuthResponse> {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: provider,
      options: {
        redirectTo: window.location.origin
      }
    });

    if (error) throw error;
    
    // Note: signInWithOAuth redirects, so we won't get user data immediately here
    // but the session will be recovered by useAuth on redirect back.
    // For the sake of the interface, we return an empty-ish response or throw
    // but since it redirects, this line is rarely reached.
    return { uid: '', email: '' };
  }

  async logout(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }

  async signUp(credentials: AuthCredentials): Promise<AuthResponse> {
    if (!credentials.email || !credentials.password) {
      throw new Error('Thiếu email hoặc mật khẩu');
    }

    const { data, error } = await supabase.auth.signUp({
      email: credentials.email,
      password: credentials.password,
      options: {
        emailRedirectTo: window.location.origin
      }
    });

    if (error) throw error;
    if (!data.user) throw new Error('Không thể tạo tài khoản');

    return {
      uid: data.user.id,
      email: data.user.email || '',
      metadata: data.user.user_metadata
    };
  }

  async getCurrentUser(): Promise<AuthResponse | null> {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error || !user) return null;

    return {
      uid: user.id,
      email: user.email || '',
      metadata: user.user_metadata
    };
  }

  async changePassword(newPassword: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });
    if (error) throw error;
  }

  async resetPassword(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  }

  async inviteUser(email: string, role: string, metadata?: Record<string, any>): Promise<AuthResponse> {
    // In a real app, this would use a secure Edge Function or Admin API.
    // Here we use a simplified signUp flow as a placeholder.
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/welcome`,
        data: { ...metadata, role, must_change_password: true }
      }
    });

    if (error) throw error;
    
    return {
      uid: '', // UID is not available until they sign in via OTP link
      email: email
    };
  }
}
